import React, { useEffect, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import FilterSearch from '@jira/platform__filter-search-with-analytics/src/FilterSearchWithAnalytics.tsx';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { BoardsTabQuery } from '@atlassian/jira-relay/src/__generated__/BoardsTabQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { TabProps } from '../../../common/types/tabs.tsx';
import { BoardsTable } from './boards-table/BoardsTable.tsx';
import { EmptyState } from './empty-state/index.tsx';
import { BoardsErrorState } from './error-state/index.tsx';
import { LoadingState } from './loading-state/index.tsx';
import { BoardsTabFooter } from './footer/index.tsx';
import messages from './messages.tsx';

type BoardTabProps = TabProps & {
	searchQuery: string;
	onInitialBoardsLoad: (hasBoards: boolean) => void;
};

// TODO: Top bar with search field (https://jplat.atlassian.net/browse/BLU-3708) and
// TODO: "Create board" button that opens existing modal (https://jplat.atlassian.net/browse/BLU-3707)
const BoardsTabWithoutErrorBoundary = ({
	maxItems,
	searchQuery,
	onInitialBoardsLoad,
}: Pick<BoardTabProps, 'maxItems' | 'searchQuery' | 'onInitialBoardsLoad'>) => {
	const cloudId = useCloudId();

	// To paginate, we need to get ALL boards to have the correct count.
	// If the 'first' or 'last' filters are used to limit the results fetched, it also affects totalCount.
	// So here we fetch a minimal amount of data, and then have the child components fetch details when they need them.
	// The alternative would be to use a "Load more" pattern instead of discrete-page style pagination.
	const data = useLazyLoadQuery<BoardsTabQuery>(
		graphql`
			query BoardsTabQuery($cloudAri: String!, $first: Int, $query: String) {
				search {
					result: search(
						experience: "jira.search"
						filters: { entities: ["jira.board"], locations: [$cloudAri] }
						first: $first
						query: $query
					) {
						totalCount
						pageInfo {
							hasNextPage
						}
						...BoardsTable
					}
				}
			}
		`,
		{ cloudAri: `ari:cloud:platform::site/${cloudId}`, first: maxItems, query: searchQuery },
	);

	const totalBoards: number = useMemo(
		() => data.search?.result?.totalCount ?? 0,
		[data?.search?.result],
	);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		onInitialBoardsLoad(totalBoards > 0);
	}, [totalBoards, onInitialBoardsLoad]);

	return (
		<>
			{totalBoards > 0 && data?.search?.result ? (
				<>
					<BoardsTable data={data?.search?.result} itemsPerPage={maxItems} />
					<BoardsTabFooter hasNextPage={data?.search?.result?.pageInfo?.hasNextPage} />
				</>
			) : (
				<EmptyState isSearch={!!searchQuery} />
			)}
		</>
	);
};

// This uses TabProps to match the other tabs, which is why it's called maxItems instead of itemsPerPage here
export const BoardsTab = ({
	maxItems,
}: Omit<TabProps, 'itemProviderResult'>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [hasInitialBoards, setHasInitialBoards] = useState(false);
	const [shouldDisplaySearch, setShouldDisplaySearch] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const onSearchValueChange = (value: string, analyticsEvent: UIAnalyticsEvent) => {
		setSearchQuery(value);
		fireUIAnalytics(analyticsEvent, 'boardsTabSearch');
	};

	// we want to hide the search input if user doesn't have any boards
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (!shouldDisplaySearch && hasInitialBoards) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
			setShouldDisplaySearch(true);
		}
	}, [hasInitialBoards, shouldDisplaySearch]);

	// TODO is it a problem that LoadingState always assumes there is data here?
	return (
		<UFOSegment name="jira-home-boards-tab">
			<JSErrorBoundary
				id="homePageYourWorkBoards"
				packageName="jiraHome"
				fallback="flag"
				teamName="jira-cosmos"
				render={({ error }) => <BoardsErrorState error={error} />}
			>
				{shouldDisplaySearch &&
					expVal('blu-8218-add-search-to-for-you-boards-tab', 'isEnabled', false) && (
						<Box xcss={searchFieldWrapperStyles.box}>
							<FilterSearch
								placeholder={formatMessage(messages.searchPlaceholder)}
								value={searchQuery}
								isCompact
								hasAutoFocus={false}
								increasedWidth={false}
								placeholderAlwaysVisible
								shouldFitContainerWidth
								onChange={onSearchValueChange}
							/>
						</Box>
					)}
				<Placeholder name="boards" fallback={<LoadingState hasData />}>
					<BoardsTabWithoutErrorBoundary
						maxItems={maxItems}
						searchQuery={searchQuery}
						onInitialBoardsLoad={setHasInitialBoards}
					/>
				</Placeholder>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

const searchFieldWrapperStyles = cssMap({
	box: {
		display: 'inline-block',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
	},
});
